import React from 'react';
import { BookOpen, Music, Calendar, CheckSquare, Layers, ArrowRight, AlertCircle, Check, Download, ShieldCheck, PlayCircle } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { SocialProofPopup } from './components/SocialProofPopup';
import { Testimonials } from './components/Testimonials';
import { useMetaPixel } from './hooks/useMetaPixel';
// Image Constants
const IMG_MAIN = 'https://cdn.magicpatterns.com/uploads/6jNNwT7f7TkiFJq5o8yGD9/1_Ebook.png';
const IMG_BONUS_1 = 'https://cdn.magicpatterns.com/uploads/udijhS7AM59mVZWBzqNmdB/bono_1.png';
const IMG_BONUS_2 = 'https://cdn.magicpatterns.com/uploads/iCDc7zu6fFwnrJ29yCiA45/bono_2.png';
const IMG_BONUS_3 = 'https://cdn.magicpatterns.com/uploads/cbGC5YFgdjAgPF26febdKv/bono_3.png';
const IMG_BONUS_4 = 'https://cdn.magicpatterns.com/uploads/edEWyB6JGE8f38GEZYLEds/bono_4.png';
const BUY_LINK = 'https://pay.hotmart.com/T104220251Q?checkoutMode=10';
export function App() {
  // Initialize Meta Pixel
  useMetaPixel('3070568583125644');
  const scrollToPurchase = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#fdfbf7] font-sans text-gray-800 selection:bg-amber-200" data-id="element-0">
      {/* Google Fonts Import */}
      <style data-id="element-1">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Patrick+Hand&display=swap');
        .font-hand { font-family: 'Patrick Hand', cursive; }
      `}</style>

      <CountdownTimer data-id="element-2" />
      <SocialProofPopup data-id="element-3" />

      {/* Spacer for fixed header */}
      <div className="h-10 md:h-12" data-id="element-4"></div>

      {/* HERO SECTION */}
      <header className="relative bg-[#1a472a] text-white overflow-hidden" data-id="element-5">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/black-scales.png')]" data-id="element-6"></div>
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-20 relative z-10" data-id="element-7">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-8">
            <div className="flex-1 text-center md:text-left" data-id="element-9">
              <div className="inline-block bg-amber-500 text-black font-bold px-4 py-1 rounded-full text-sm mb-6 transform -rotate-2 shadow-lg" data-id="element-10">
                🎵 SISTEMA COMPLETO PARA DOCENTES
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight font-hand tracking-wide text-[#fdfbf7]" data-id="element-11">
                MÚSICA ESCOLAR LISTA
              </h1>
              <p className="text-xl md:text-2xl text-amber-100 mb-8 font-medium leading-relaxed" data-id="element-12">
                El sistema que organiza{' '}
                <span className="text-amber-400 font-bold underline decoration-wavy" data-id="element-13">
                  TODO tu año de Música
                </span>{' '}
                sin improvisar clases.
              </p>
              <button onClick={scrollToPurchase} className="group bg-amber-500 hover:bg-amber-400 text-black text-lg md:text-xl font-bold py-4 px-8 rounded-xl shadow-[0_4px_0_rgb(180,83,9)] hover:shadow-[0_2px_0_rgb(180,83,9)] hover:translate-y-[2px] transition-all flex items-center justify-center gap-3 mx-auto md:mx-0 w-full md:w-auto" data-id="element-14">
                ¡QUIERO ORGANIZAR MI AÑO!
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-15" />
              </button>
            </div>
            <div className="flex-1 w-full max-w-md md:max-w-lg" data-id="element-16">
              <div className="relative group cursor-pointer" onClick={scrollToPurchase} data-id="element-17">
                <div className="absolute -inset-1 bg-amber-500 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-200" data-id="element-18"></div>
                <img src={IMG_MAIN} alt="Música Escolar Lista Pack" className="relative rounded-2xl shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-300" data-id="element-19" />
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none" data-id="element-20">
          <svg className="relative block w-[calc(100%+1.3px)] h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-21">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-[#fdfbf7]" data-id="element-22"></path>
          </svg>
        </div>
      </header>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 max-w-4xl mx-auto" data-id="element-23">
        <div className="text-center mb-12" data-id="element-24">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a472a] mb-4 font-hand" data-id="element-25">
            👩‍🏫 Profe de Música…
          </h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full" data-id="element-26"></div>
        </div>

        <div className="space-y-6 text-lg text-gray-700" data-id="element-27">
          {['¿Sientes que cada clase es empezar de cero?', 'Que tienes ideas sueltas… pero no un sistema claro.', 'Que algunas clases funcionan… y otras se sienten improvisadas.', 'Que entre ritmo, canciones, instrumentos y teoría… terminas resolviendo sobre la marcha.', 'Y cuando llega el momento de evaluar… no sabes bien qué criterios usar.'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm border-l-4 border-amber-500" data-id="element-28">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" data-id="element-29" />
              <p className="font-medium" data-id="element-30">{item}</p>
            </div>)}
        </div>

        <div className="mt-10 bg-red-50 border border-red-100 p-6 rounded-xl text-center" data-id="element-31">
          <p className="font-bold text-red-800 text-xl mb-2" data-id="element-32">
            👉 No es falta de creatividad. 👉 No es falta de vocación.
          </p>
          <p className="text-2xl font-black text-red-600 uppercase" data-id="element-33">
            👉 Es falta de estructura organizada.
          </p>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 bg-gray-100 px-4" data-id="element-34">
        <div className="max-w-4xl mx-auto" data-id="element-35">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-10" data-id="element-36">
            ❌ El problema no es enseñar Música
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-37">
            <div data-id="element-38">
              <p className="text-xl text-gray-700 mb-6" data-id="element-39">
                El problema es que nadie te dio el año resuelto. Te dejaron con:
              </p>
              <ul className="space-y-3" data-id="element-40">
                {['Contenidos dispersos', 'Actividades aisladas', 'Evaluaciones poco claras', 'Mucha práctica… pero sin planificación real'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-600" data-id="element-41">
                    <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-42"></div>
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg transform rotate-1" data-id="element-43">
              <p className="font-bold text-gray-500 mb-4 uppercase tracking-wider text-sm" data-id="element-44">
                Y eso genera:
              </p>
              <div className="space-y-4" data-id="element-45">
                <div className="flex items-center gap-3 text-red-600 font-bold text-lg" data-id="element-46">
                  <span className="text-2xl" data-id="element-47">😓</span> Clases desordenadas
                </div>
                <div className="flex items-center gap-3 text-red-600 font-bold text-lg" data-id="element-48">
                  <span className="text-2xl" data-id="element-49">😓</span> Tiempo perdido
                </div>
                <div className="flex items-center gap-3 text-red-600 font-bold text-lg" data-id="element-50">
                  <span className="text-2xl" data-id="element-51">😓</span> Estrés innecesario
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-20 px-4 bg-[#1a472a] text-white relative overflow-hidden" data-id="element-52">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500 rounded-full filter blur-[100px] opacity-20" data-id="element-53"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-54">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-hand text-amber-400" data-id="element-55">
            ✅ LA SOLUCIÓN: UN SISTEMA COMPLETO
          </h2>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-gray-200" data-id="element-56">
            No es un ebook suelto. Es tu área de Música organizada.
          </p>

          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20 mb-12" data-id="element-57">
            <p className="text-lg md:text-xl mb-8" data-id="element-58">
              <span className="font-bold text-amber-400" data-id="element-59">
                Música Escolar Lista
              </span>{' '}
              es un MegaPack docente que te entrega:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-left" data-id="element-60">
              {['Planificación anual completa', 'Secuencias de clases listas', 'Actividades prácticas', 'Material imprimible', 'Evaluaciones alineadas'].map((item, i) => <div key={i} className="flex items-center gap-3 bg-[#1a472a] p-3 rounded-lg border border-green-700 shadow-lg" data-id="element-61">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-62" />
                  <span className="font-medium" data-id="element-63">{item}</span>
                </div>)}
            </div>
            <p className="mt-8 text-2xl font-hand font-bold text-amber-300" data-id="element-64">
              Todo conectado. Todo coherente. Todo listo.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED SECTION */}
      <section className="py-20 px-4 max-w-6xl mx-auto" data-id="element-65">
        <h2 className="text-4xl font-bold text-center text-[#1a472a] mb-16 font-hand" data-id="element-66">
          📚 ¿QUÉ INCLUYE EL SISTEMA?
        </h2>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-67">
          <div className="order-2 md:order-1 space-y-6" data-id="element-68">
            {[{
            icon: <Calendar className="w-6 h-6 text-blue-600" data-id="element-69" />,
            title: 'TOMO 1 — Planificación Anual',
            desc: 'El mapa completo del año escolar.',
            color: 'bg-blue-50 border-blue-200'
          }, {
            icon: <Layers className="w-6 h-6 text-orange-600" data-id="element-70" />,
            title: 'TOMO 2 — Secuencias Didácticas',
            desc: 'Clases paso a paso.',
            color: 'bg-orange-50 border-orange-200'
          }, {
            icon: <BookOpen className="w-6 h-6 text-red-600" data-id="element-71" />,
            title: 'TOMO 3 — Actividades y Fichas',
            desc: 'Material listo para usar.',
            color: 'bg-red-50 border-red-200'
          }, {
            icon: <CheckSquare className="w-6 h-6 text-green-600" data-id="element-72" />,
            title: 'TOMO 4 — Evaluaciones y Rúbricas',
            desc: 'Criterios claros, sin improvisar.',
            color: 'bg-green-50 border-green-200'
          }, {
            icon: <Music className="w-6 h-6 text-purple-600" data-id="element-73" />,
            title: 'TOMO 5 — Proyectos y Refuerzo',
            desc: 'Para distintos ritmos de aprendizaje.',
            color: 'bg-purple-50 border-purple-200'
          }].map((item, i) => <div key={i} className={`flex items-center gap-4 p-4 rounded-xl border-2 ${item.color} transition-transform hover:scale-[1.02]`} data-id="element-74">
                <div className="bg-white p-3 rounded-full shadow-sm" data-id="element-75">
                  {item.icon}
                </div>
                <div data-id="element-76">
                  <h3 className="font-bold text-lg text-gray-900" data-id="element-77">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-78">{item.desc}</p>
                </div>
              </div>)}
          </div>
          <div className="order-1 md:order-2 flex justify-center" data-id="element-79">
            <img src={IMG_MAIN} alt="Contenido del pack" className="rounded-xl shadow-2xl w-full max-w-md cursor-pointer hover:scale-105 transition-transform duration-300" onClick={scrollToPurchase} data-id="element-80" />
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-20 px-4 bg-amber-50 border-y-4 border-amber-200" data-id="element-81">
        <div className="max-w-6xl mx-auto" data-id="element-82">
          <div className="text-center mb-12" data-id="element-83">
            <span className="bg-red-600 text-white font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider animate-pulse" data-id="element-84">
              Gratis por hoy
            </span>
            <h2 className="text-4xl font-bold text-[#1a472a] mt-4 font-hand" data-id="element-85">
              🎁 BONOS INCLUIDOS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-86">
            {[{
            img: IMG_BONUS_1,
            title: 'Banco de Canciones Escolares'
          }, {
            img: IMG_BONUS_2,
            title: 'Juegos Musicales de Emergencia'
          }, {
            img: IMG_BONUS_3,
            title: 'Tarjetas de Ritmo y Sonido'
          }, {
            img: IMG_BONUS_4,
            title: 'Kit de Evaluaciones Rápidas'
          }].map((bonus, i) => <div key={i} className="bg-white p-4 rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer group" onClick={scrollToPurchase} data-id="element-87">
                <div className="overflow-hidden rounded-lg mb-4 relative" data-id="element-88">
                  <div className="absolute top-2 right-2 bg-amber-500 text-xs font-bold px-2 py-1 rounded text-black z-10" data-id="element-89">
                    VALOR: $15 USD
                  </div>
                  <img src={bonus.img} alt={bonus.title} className="w-full h-auto transform group-hover:scale-110 transition-transform duration-500" data-id="element-90" />
                </div>
                <h3 className="font-bold text-center text-gray-800 leading-tight" data-id="element-91">
                  {bonus.title}
                </h3>
                <p className="text-center text-green-600 font-bold text-sm mt-2" data-id="element-92">
                  ¡GRATIS!
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* TRANSFORMATION SECTION */}
      <section className="py-20 px-4 max-w-5xl mx-auto" data-id="element-93">
        <h2 className="text-4xl font-bold text-center text-[#1a472a] mb-12 font-hand" data-id="element-94">
          🎯 ¿QUÉ CAMBIA PARA TI?
        </h2>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-95">
          <div className="bg-red-50 p-8 rounded-2xl border border-red-100" data-id="element-96">
            <h3 className="text-2xl font-bold text-red-800 mb-6 flex items-center gap-2" data-id="element-97">
              <span className="text-3xl" data-id="element-98">🚫</span> Antes:
            </h3>
            <ul className="space-y-4" data-id="element-99">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-100">
                <span className="text-red-500 font-bold" data-id="element-101">❌</span> Improvisación
                constante
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-102">
                <span className="text-red-500 font-bold" data-id="element-103">❌</span> Clases
                desordenadas
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-104">
                <span className="text-red-500 font-bold" data-id="element-105">❌</span> Evaluar “a
                ojo”
              </li>
            </ul>
          </div>

          <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg relative overflow-hidden" data-id="element-106">
            <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-107">
              TU NUEVA REALIDAD
            </div>
            <h3 className="text-2xl font-bold text-green-800 mb-6 flex items-center gap-2" data-id="element-108">
              <span className="text-3xl" data-id="element-109">✅</span> Después:
            </h3>
            <ul className="space-y-4" data-id="element-110">
              <li className="flex items-center gap-3 text-gray-800 font-medium" data-id="element-111">
                <span className="text-green-600 font-bold" data-id="element-112">✔</span> Secuencia
                clara
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-medium" data-id="element-113">
                <span className="text-green-600 font-bold" data-id="element-114">✔</span> Clases
                fluidas
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-medium" data-id="element-115">
                <span className="text-green-600 font-bold" data-id="element-116">✔</span>{' '}
                Evaluaciones con criterio
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-medium" data-id="element-117">
                <span className="text-green-600 font-bold" data-id="element-118">✔</span> Menos carga
                mental
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* BENEFITS & IDEAL FOR */}
      <section className="py-16 px-4 bg-[#2d3b2d] text-white" data-id="element-119">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-120">
          <div data-id="element-121">
            <h3 className="text-2xl font-bold text-amber-400 mb-6 flex items-center gap-3" data-id="element-122">
              <span className="bg-amber-500/20 p-2 rounded-lg" data-id="element-123">🧠</span> Este
              sistema te permite:
            </h3>
            <ul className="space-y-4" data-id="element-124">
              {['Enseñar con coherencia', 'Tener clases dinámicas', 'Organizar teoría y práctica', 'Trabajar todo el año sin improvisar'].map((item, i) => <li key={i} className="flex items-center gap-3 border-b border-green-800 pb-2" data-id="element-125">
                  <Check className="text-amber-400 w-5 h-5" data-id="element-126" />
                  {item}
                </li>)}
            </ul>
            <p className="mt-6 text-lg italic text-green-200" data-id="element-127">
              "Música deja de ser 'lo que salga' y se convierte en área
              organizada."
            </p>
          </div>

          <div data-id="element-128">
            <h3 className="text-2xl font-bold text-amber-400 mb-6 flex items-center gap-3" data-id="element-129">
              <span className="bg-amber-500/20 p-2 rounded-lg" data-id="element-130">👩‍🏫</span> Ideal
              para docentes que:
            </h3>
            <ul className="space-y-4" data-id="element-131">
              {['Trabajan en primaria o secundaria', 'Sienten que el área es muy amplia', 'Quieren orden y claridad', 'Necesitan clases listas'].map((item, i) => <li key={i} className="flex items-center gap-3 border-b border-green-800 pb-2" data-id="element-132">
                  <Check className="text-amber-400 w-5 h-5" data-id="element-133" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 px-4 bg-white" data-id="element-134">
        <div className="max-w-6xl mx-auto" data-id="element-135">
          <h2 className="text-4xl font-bold text-center text-[#1a472a] mb-4 font-hand" data-id="element-136">
            Lo que dicen otros profes
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto" data-id="element-137">
            Docentes de toda Latinoamérica ya están organizando su año con este
            sistema.
          </p>
          <Testimonials data-id="element-138" />
        </div>
      </section>

      {/* PURCHASE SECTION */}
      <section id="compra" className="py-20 px-4 bg-gradient-to-b from-amber-50 to-white" data-id="element-139">
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-amber-400 relative" data-id="element-140">
          {/* Badge */}
          <div className="absolute top-0 right-0 bg-red-600 text-white font-bold px-6 py-2 rounded-bl-2xl z-10" data-id="element-141">
            OFERTA ESPECIAL
          </div>

          <div className="p-8 md:p-12 text-center" data-id="element-142">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a472a] mb-2 font-hand" data-id="element-143">
              MÚSICA ESCOLAR LISTA
            </h2>
            <p className="text-gray-500 mb-8" data-id="element-144">
              Acceso inmediato • Descarga digital
            </p>

            <div className="flex justify-center items-center gap-6 mb-8" data-id="element-145">
              <div className="text-gray-400 line-through text-2xl font-bold" data-id="element-146">
                $47 USD
              </div>
              <div className="text-6xl font-black text-[#1a472a] tracking-tight" data-id="element-147">
                $13.99
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-6 mb-8 border border-green-100" data-id="element-148">
              <p className="font-bold text-green-800 mb-4" data-id="element-149">
                INCLUYE TODO EL PACK + 4 BONOS DE REGALO
              </p>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-150">
                <span className="bg-white px-3 py-1 rounded-full text-sm text-gray-600 shadow-sm" data-id="element-151">
                  📚 5 Tomos Completos
                </span>
                <span className="bg-white px-3 py-1 rounded-full text-sm text-gray-600 shadow-sm" data-id="element-152">
                  🎵 Banco de Canciones
                </span>
                <span className="bg-white px-3 py-1 rounded-full text-sm text-gray-600 shadow-sm" data-id="element-153">
                  🎲 Juegos Musicales
                </span>
                <span className="bg-white px-3 py-1 rounded-full text-sm text-gray-600 shadow-sm" data-id="element-154">
                  📋 Evaluaciones
                </span>
              </div>
            </div>

            <a href={BUY_LINK} className="block w-full bg-[#1a472a] hover:bg-[#143620] text-white text-xl md:text-2xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all mb-6 animate-bounce-subtle" data-id="element-155">
              ¡SÍ, QUIERO EL SISTEMA COMPLETO!
            </a>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6" data-id="element-156">
              <ShieldCheck className="w-4 h-4" data-id="element-157" />
              <span data-id="element-158">Pago 100% Seguro y Encriptado</span>
            </div>

            <div className="border-t border-gray-100 pt-6" data-id="element-159">
              <div className="flex items-center justify-center gap-4" data-id="element-160">
                <img src="https://cdn.magicpatterns.com/uploads/6jNNwT7f7TkiFJq5o8yGD9/1_Ebook.png" alt="Miniatura" className="w-16 h-16 object-cover rounded shadow-sm" data-id="element-161" />
                <div className="text-left" data-id="element-162">
                  <p className="font-bold text-gray-800" data-id="element-163">
                    Garantía de satisfacción
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-164">
                    Material creado para facilitar tu trabajo, no complicarlo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12 text-gray-500 text-sm" data-id="element-165">
          <p data-id="element-166">© 2024 Música Escolar Lista. Todos los derechos reservados.</p>
          <p className="mt-2" data-id="element-167">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
          </p>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 p-4 md:hidden z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" data-id="element-168">
        <button onClick={scrollToPurchase} className="w-full bg-amber-500 text-black font-bold py-3 rounded-lg shadow-md" data-id="element-169">
          ¡QUIERO ORGANIZARME POR $13.99!
        </button>
      </div>
    </div>;
}